import { QuickStartPageEnum } from '@rightship-tech/message-centre-app/models/common.models';
import { autoinject } from "aurelia-framework";
import { ToggleModel } from "@rightship-tech/common-ui/components/comp-toggle/comp-toggle";
import { RightshipInspectionService } from "../../../services/rightship-inspection.service";
import { RightShipInspectionDetailsModel, RightShipInspectionStatusEnum } from "../../../models/rightship-inspections.models";
import { ActionButtonItem } from "../../../models/common.models";
import { RsInspectionRouteConstants } from "../../../constants/route.constants";
import { CacheClientService, CacheLocation } from "@rightship-tech/common-ui/services/basic/client-caching.service";
import { Router } from 'aurelia-router';
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";

@autoinject
export class Inspection {

    private _encryptedData: string;
    public inspectionModel: RightShipInspectionDetailsModel;
    public tooltipViewModel: ToggleModel;
    public readonly maxWidthTooltip: number = 436;
    public actions: ActionButtonItem[] = [];
    public quickStartPages: number[] = [
        QuickStartPageEnum.IntroduceMessageCenter, 
        QuickStartPageEnum.SendReceiveEmail, 
        QuickStartPageEnum.ActionAvailable
    ];
    public readonly quickStartThread: { subject: string, latestConversaionSummary: string, latestSenderEmail: string } = {
        subject: "Quickstart Guide to the Message Centre for Inspections",
        latestConversaionSummary: "Welcome to the Message Centre! The Message Centre is a centralised hub that keeps track of and organises correspondences for your...",
        latestSenderEmail: "welcome@rightship.com",
    };
    public task: { 
        taskId: number, 
        entityType: any, 
        vesselName: string, 
        vesselImo: number | string, 
        subtaskId?: number, 
        description: string, 
        emailIdentifier: string,
        emailIdentifierTypeName: string,
        encryptedParams: string,
        isShipManager: boolean
        isInspectionCompany: boolean
        isFreelancer: boolean,
        assignmentStatusActive: boolean,
        reviseProposal: () => void,
        withdrawInspection: () => void,
        changeInspectionDate: () => void,
        cancelInspection: () => void
    };
    public allowInitNewThread: boolean = true;    
    constructor(private router: Router, private cacheClientService: CacheClientService,
        private rightShipInspectionService: RightshipInspectionService) {
    }

    async activate(params: { encryptedData: string }) {

        //Set site name in local storage
        this.cacheClientService.put('site-name', "Message Center", CacheLocation.LocalStorage);
        this._encryptedData = decodeURIComponent(params.encryptedData);
    }

    async attached() {
        //Set site name in local storage
        await this.cacheClientService.put('site-name', "Message Center", CacheLocation.LocalStorage);
        this.inspectionModel = await this.rightShipInspectionService.getRightShipInspectionDetails(this._encryptedData);
        if (this.inspectionModel.validationResult && this.inspectionModel.validationResult.hasError) {
            this.router.navigateToRoute(RsInspectionRouteConstants.InspectionClosed, {
                vesselName: this.inspectionModel.vesselDetails.vesselName,
                vesselImo: this.inspectionModel.vesselDetails.vesselImo,
                portName: this.inspectionModel.portAndOperationalDetails.port,
                country: this.inspectionModel.portAndOperationalDetails.country,
                requestedInspectionDate: this.inspectionModel.portAndOperationalDetails.requestedInspectionDate,
                rsInspectionId: this.inspectionModel.rightShipInspectionId
            })
        }
        
        const isInspectionCompany = this.inspectionModel.emailIdentifierTypeName == "Inspection Company";
        const isFreelancer = this.inspectionModel.emailIdentifierTypeName == "Freelancer";
        const isShipManager = this.inspectionModel.emailIdentifierTypeName=="Correspondence" || this.inspectionModel.emailIdentifierTypeName=="Technical Manager";
        this.setActionItems([
        {
            isVisible: true,
            text: "Learn more",
            description: "Welcome to RightShip Message Centre!",
            url: ""
        },
        {   
            isVisible: (isFreelancer || isInspectionCompany) && this.inspectionModel.assignmentStatusActive && this.inspectionModel.statusId !== RightShipInspectionStatusEnum.Cancelled && this.inspectionModel.statusId !== RightShipInspectionStatusEnum.Acceptable && this.inspectionModel.statusId !== RightShipInspectionStatusEnum.Unacceptable,
            text: "Revise proposal",
            description: "If you require a change of inspector or estimated cost, submit a revision.",
            click: () => this.reviseProposal(),
        },
        {
            click: () => this.withdrawInspection(),
            isVisible: (isFreelancer || isInspectionCompany) && this.inspectionModel.assignmentStatusActive && this.inspectionModel.statusId !== RightShipInspectionStatusEnum.Cancelled && this.inspectionModel.statusId !== RightShipInspectionStatusEnum.Acceptable && this.inspectionModel.statusId !== RightShipInspectionStatusEnum.Unacceptable,
            description: "If you cannot service the inspection, submit a withdrawal.",
            text: "Withdraw from inspection"
        },
        {
            click: () => this.changeInspectionDate(),
            isVisible: isShipManager && this.inspectionModel.statusId !== RightShipInspectionStatusEnum.Cancelled && this.inspectionModel.statusId !== RightShipInspectionStatusEnum.Acceptable && this.inspectionModel.statusId !== RightShipInspectionStatusEnum.Unacceptable,
            text: "Change Inspection Date",
            description: "If you cannot make the inspection date, submit a change."
        },
        {
            click: () => this.cancelInspection(),
            isVisible: isShipManager && this.inspectionModel.statusId !== RightShipInspectionStatusEnum.Cancelled && this.inspectionModel.statusId !== RightShipInspectionStatusEnum.Acceptable && this.inspectionModel.statusId !== RightShipInspectionStatusEnum.Unacceptable,
            text: "Cancel inspection",
            description: "If you cannot proceed with the inspection, submit a cancellation."
        }]);

        if(this.inspectionModel) {
            this.task = {
                description: this.inspectionModel.vesselDetails.dateOfBuild,
                entityType: EntityTypes.RightShipInspection,
                taskId: this.inspectionModel.rightShipInspectionId,
                subtaskId: this.inspectionModel.subtaskId,
                vesselImo: this.inspectionModel.vesselDetails.vesselImo,
                vesselName: this.inspectionModel.vesselDetails.vesselName,
                emailIdentifier: this.inspectionModel.emailIdentifier,
                emailIdentifierTypeName: this.inspectionModel.emailIdentifierTypeName,
                encryptedParams: this._encryptedData,
                isShipManager: isShipManager,
                isFreelancer: isFreelancer,
                isInspectionCompany: isInspectionCompany,
                assignmentStatusActive: this.inspectionModel.assignmentStatusActive,
                reviseProposal: this.reviseProposal,
                withdrawInspection: this.withdrawInspection,
                changeInspectionDate: this.changeInspectionDate,
                cancelInspection: this.cancelInspection
            }

            this.allowInitNewThread = this.inspectionModel.statusId !== RightShipInspectionStatusEnum.Acceptable 
                                                && this.inspectionModel.statusId !== RightShipInspectionStatusEnum.Unacceptable;
        }
    }

    private reviseProposal = () => {
        this.router.navigateToRoute(RsInspectionRouteConstants.Proposal, { 
            encryptedData: encodeURIComponent(this._encryptedData),
            reviseOffer: true
        });
    }

    private withdrawInspection = () => {
        this.router.navigateToRoute(RsInspectionRouteConstants.WithdrawInspection, { 
            encryptedData: encodeURIComponent(this._encryptedData)
        });
    }

    private changeInspectionDate = () => {
        this.router.navigateToRoute(RsInspectionRouteConstants.ChangeInspectionDate, { 
            encryptedData: encodeURIComponent(this._encryptedData)
        });
    }

    private cancelInspection = () => {

        //If inspection is already cancelled, redirect to not found page
        if (this.inspectionModel.statusId !== RightShipInspectionStatusEnum.Cancelled) {
            this.router.navigateToRoute(RsInspectionRouteConstants.CancelInspection, { 
                encryptedData: encodeURIComponent(this._encryptedData)
            });
        } else {
            window.location.href = RsInspectionRouteConstants.NotFound;
        }
    }

    private setActionItems = (items: ActionButtonItem[]) => {
        this.actions = items;
    }
}